import {
  Component, EventEmitter, Input, OnDestroy,
  Output
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  catchError,
  debounceTime,
  map, Observable, of, startWith, Subject, switchMap, take, 
  takeUntil
} from 'rxjs';
import { SurveysService } from 'src/app/surveys/surveys.service';
import { isNullOrEmpty } from 'src/app/utils/is-empty-string/is-empty-string';
import { getUserMessageFromHttpErrorResponse } from 'src/app/utils/handle-error/handle-error';
import { isSpecialCharacter } from 'src/app/utils/is-special-character/is-special-character';
import { FormControl } from '@angular/forms';
import { DialogComponent, DialogData, DialogMode } from '../dialog/dialog.component';

@Component({
  selector: 'app-tags-autocomplete-dropdown',
  templateUrl: './tags-autocomplete-dropdown.component.html',
  styleUrls: ['./tags-autocomplete-dropdown.component.scss']
})
export class TagsAutocompleteDropdownComponent implements OnDestroy {
  @Input() tagList: string[] = [];

  @Output() tagListChanged = new EventEmitter<string[]>();

  searchTagTerm = '';

  searchTagResults = [];

  searchTagMessage = '';

  readonly searchTagsTitle = 'Search tags error';

  readonly MIN_TAG_SEARCH_LENGTH = 2;

  readonly MIN_TAG_LENGTH = 3;

  readonly MAX_TAG_LENGTH = 30;

  autoCompleteControl = new FormControl();

  filteredAutoCompleteOptions: Observable<string[]>;

  private onDestroy$ = new Subject<void>(); // used to unsubscribe from observables on component destroy

  isResultLoading = false;

  isAddItemAvailableAfterDebounce = true;

  constructor(
    private dialog: MatDialog,
    private surveyService: SurveysService,
  ) {
    this.filteredAutoCompleteOptions = this.autoCompleteControl.valueChanges.pipe(
      takeUntil(this.onDestroy$),
      startWith(''),
      debounceTime(100),
      switchMap((value) => {
        this.isResultLoading = true;
        return this.getTagSearchResults(value).pipe(
          map((results) => {
            if (results.length > 0) {
              this.clearSearchTagMessage();
            }

            if (this.autoCompleteControl.value?.length > 2 && results.length == 0) {
              this.displaySearchTagMessage('No matching tags found');
            }

            this.isResultLoading = false;
            return results;
          }),
          catchError((error) => {
            this.isResultLoading = false;
            throw error;
          })
        );
      }),
    );
  }

  getTagSearchResults(searchTerm: string): Observable<string[]> {
    if (isNullOrEmpty(searchTerm)) {
      return of([]);
    }

    if (isSpecialCharacter(searchTerm)) {
      this.displaySearchTagMessage('Tag name should only contain alphanumeric characters');
      return of([]);
    }

    if (searchTerm.length < 3) {
      return of([]);
    }

    return this.surveyService.searchTags(searchTerm).pipe(
      map((response) => response['searchResults'].filter((tag) => !this.tagList.includes(tag))),
      catchError((error) => {
        this.dialog.open(DialogComponent, {
          data: <DialogData>{
            title: this.searchTagsTitle,
            message: getUserMessageFromHttpErrorResponse(error),
            mode: DialogMode.Error,
          },
          disableClose: true,
          backdropClass: 'dialog-backdrop',
        });
        return of([]); // Return an empty array in case of an error
      }),
    );
  }

  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  removeTag(tag: string) {
    this.tagList = this.tagList.filter((item: string) => item !== tag);
    this.autoCompleteControl.setValue('');
    this.tagListChanged.emit(this.tagList);
  }

  clearSearchTagMessage() {
    this.searchTagMessage = '';
  }

  displaySearchTagMessage(message: string) {
    this.searchTagMessage = message;
    this.searchTagResults = [];
  }

  private doesTagExist(tag: string, tagList: string[]): boolean {
    return !!tagList?.find((item: string) => item === tag);
  }

  addItem(tag: string) {
    // A short debounce is used because we depend on a keypress.enter event to allow the user to add a tag that doesn't exist yet
    // but this event also fires if they use enter to select a highlighted option (which results in addItem being called twice).
    if (!this.isAddItemAvailableAfterDebounce) {
      return;
    }
    this.isAddItemAvailableAfterDebounce = false;
    setTimeout(() => {
      this.isAddItemAvailableAfterDebounce = true;
    }, 20);

    this.autoCompleteControl.setValue('');
    // Validate tag length
    if (tag.length < this.MIN_TAG_LENGTH || tag.length > this.MAX_TAG_LENGTH) {
      this.displaySearchTagMessage(`Tag length must be between ${this.MIN_TAG_LENGTH} and  ${this.MAX_TAG_LENGTH} characters long`);
      return;
    }
    // Validate to not allow special characters
    if (isSpecialCharacter(tag)) {
      this.displaySearchTagMessage('Tag name should only contain alphanumeric characters');
      return;
    }
    // Add to tag list if it does not already exist
    if (this.doesTagExist(tag, this.tagList)) {
      this.displaySearchTagMessage(`Tag name '${tag}' already exists`);
    } else {
      this.clearSearchTagMessage();
      this.tagList.push(tag);
    }
    this.tagListChanged.emit(this.tagList);
  }
}
